import AppComponent from '../base/app-component.ts';
import { templateParseToElement } from '../lib/template-parse.ts';
import type { ModalitaRicezione } from '@/types/entities/index.ts';

interface DatiForm {
  cassa: string;
  tipo: string;
  sigla: string;
  siglaOrigine: string;
  modalitaRicezione: string;
  dataDisdetta: string;
  note: string;
}

/** Disdetta salvata: i campi che il form mostra */
interface DisdettaSalvata {
  cassa: string;
  tipo: string;
  sigla: string | null;
  siglaOrigine?: string | null;
  modalitaRicezione?: string | null;
  dataDisdetta: string;
  note: string | null;
}

interface LavoratoreRicerca {
  id: number;
  cognome: string;
  nome: string;
  codiceFiscale: string | null;
}

/**
 * wc-nuova-disdetta — Inserimento e modifica di una disdetta sindacale.
 *
 * Con l'attributo `disdetta-id` modifica una disdetta esistente: niente ricerca, si parte dal form.
 *
 * Form a 2 step caricato in iframe (modale Bootstrap):
 *   Step 1 → Ricerca lavoratore (debounce 300ms, min 3 char)
 *   Step 2 → Form disdetta (cassa, tipo, sigla/ricevente, modalità, data, note) + verifica warning
 *
 * Tipo predefinito Generico (preventivo 06/09/2026): sindacato ricevente e modalità
 * di ricezione obbligatori, la sigla resta vuota finché non arriva il file cassa.
 * Inviata/Ricevuta: sigla controparte obbligatoria, modalità facoltativa.
 *
 * Successo: postMessage al parent (iframe) per chiusura modale.
 */
export class NuovaDisdetta extends AppComponent {
  static override componentName = 'wc-nuova-disdetta';

  private disdettaId = 0;
  private lavoratoreSelezionato: LavoratoreRicerca | null = null;
  private warningsVisibili = false;
  private searchTimeout: ReturnType<typeof setTimeout> | null = null;

  protected override get templateId() { return 'nuova-disdetta'; }

  protected override afterRender(): void {
    this.setupRicerca();
    this.setupCambiaLavoratore();
    this.setupForm();
    this.setupTipoToggle();
    this.disdettaId = this.attributeNumber('disdetta-id');
    if (this.disdettaId) this.caricaDisdetta();
  }

  // --- Modifica: si parte dal form compilato ---

  private async caricaDisdetta(): Promise<void> {
    const stepRicerca = this.$('[data-step="ricerca"]');
    if (!stepRicerca) return;
    templateParseToElement('shared-loading-state', stepRicerca, {});

    const disdetta = await this.api.disdette.dettaglio(this.disdettaId);
    const lavoratore = disdetta.ok ? await this.api.lavoratori.dettaglio(disdetta.data.lavoratoreId) : null;
    if (!disdetta.ok || !lavoratore?.ok) {
      const message = !disdetta.ok ? disdetta.error : lavoratore && !lavoratore.ok ? lavoratore.error : '';
      templateParseToElement('shared-error-state', stepRicerca, { message });
      return;
    }

    this.lavoratoreSelezionato = lavoratore.data;
    this.$('[data-action="cambia-lavoratore"]')?.classList.add('d-none');
    this.compilaForm(disdetta.data);
    this.mostraForm();
  }

  /** Prima il tipo: applicaTipo azzera sigla e ricevente non pertinenti */
  private compilaForm(d: DisdettaSalvata): void {
    const imposta = (campo: string, valore: string | null | undefined): void => {
      const el = this.$(`[data-field="${campo}"]`) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
      if (el) el.value = valore ?? '';
    };
    const generico = d.tipo === 'GENERICO' || d.tipo === 'RIASSOCIATA';
    const tipo = generico ? 'GENERICO' : d.tipo;
    imposta('tipo', tipo);
    this.applicaTipo(tipo);
    imposta('cassa', d.cassa);
    imposta('sigla', generico ? '' : d.sigla);
    imposta('siglaOrigine', d.siglaOrigine);
    imposta('modalitaRicezione', d.modalitaRicezione);
    imposta('dataDisdetta', d.dataDisdetta);
    imposta('note', d.note);
  }

  // --- Step 1: Ricerca lavoratore ---

  private setupRicerca(): void {
    const input = this.$('[data-search-input]') as HTMLInputElement | null;
    if (!input) return;

    this.listen(input, 'input', () => {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      const q = input.value.trim();
      if (q.length < 3) {
        const results = this.$('[data-search-results]');
        if (results) results.innerHTML = '';
        return;
      }
      this.searchTimeout = setTimeout(() => this.doSearch(q), 300);
    });
  }

  private async doSearch(q: string): Promise<void> {
    const container = this.$('[data-search-results]');
    if (!container) return;

    templateParseToElement('shared-loading-state', container, {});

    const result = await this.api.lavoratori.cerca(q);
    if (!result.ok) {
      templateParseToElement('shared-error-state', container, { message: result.error });
      return;
    }

    const lavoratori = result.data.risultati;
    templateParseToElement('shared-lavoratori-risultati', container, { lavoratori, action: 'seleziona-lavoratore' });

    this.delegate.offAll('click', '[data-action="seleziona-lavoratore"]');
    this.delegate.on('click', '[data-action="seleziona-lavoratore"]', (e: Event) => {
      const row = (e.target as HTMLElement).closest('[data-action="seleziona-lavoratore"]') as HTMLElement;
      const id = Number(row.dataset.id);
      const lav = lavoratori.find(l => l.id === id);
      if (lav) {
        this.lavoratoreSelezionato = lav;
        this.mostraForm();
      }
    });
  }

  // --- Step 2: Form disdetta ---

  private mostraForm(): void {
    const stepRicerca = this.$('[data-step="ricerca"]');
    const stepForm = this.$('[data-step="form"]');
    if (stepRicerca) stepRicerca.style.display = 'none';
    if (stepForm) stepForm.style.display = '';

    const info = this.$('[data-lavoratore-info]');
    if (info && this.lavoratoreSelezionato) {
      const l = this.lavoratoreSelezionato;
      info.innerHTML = `<strong>${l.cognome} ${l.nome}</strong>` +
        (l.codiceFiscale ? ` &mdash; <code>${l.codiceFiscale}</code>` : '');
    }

    this.resetWarnings();
  }

  private setupCambiaLavoratore(): void {
    this.delegate.on('click', '[data-action="cambia-lavoratore"]', () => {
      this.lavoratoreSelezionato = null;
      this.resetWarnings();

      const stepRicerca = this.$('[data-step="ricerca"]');
      const stepForm = this.$('[data-step="form"]');
      if (stepRicerca) stepRicerca.style.display = '';
      if (stepForm) stepForm.style.display = 'none';

      const input = this.$('[data-search-input]') as HTMLInputElement | null;
      if (input) { input.value = ''; input.focus(); }
      const results = this.$('[data-search-results]');
      if (results) results.innerHTML = '';
    });
  }

  private setupForm(): void {
    const form = this.$('[data-action="salva"]') as HTMLFormElement | null;
    if (!form) return;

    this.listen(form, 'submit', (e: Event) => {
      e.preventDefault();
      if (this.warningsVisibili) return;
      this.verificaESalva();
    });

    this.delegate.on('click', '[data-action="salva-comunque"]', () => this.salva());
    this.delegate.on('click', '[data-action="modifica"]', () => this.resetWarnings());
  }

  private setupTipoToggle(): void {
    const tipoSelect = this.$('[data-field="tipo"]') as HTMLSelectElement | null;
    if (!tipoSelect) return;

    this.listen(tipoSelect, 'change', () => this.applicaTipo(tipoSelect.value));
    this.applicaTipo(tipoSelect.value);
  }

  private applicaTipo(tipo: string): void {
    const isGenerico = tipo === 'GENERICO';
    const gruppoSigla = this.$('[data-gruppo-sigla]') as HTMLElement | null;
    const gruppoRicevente = this.$('[data-gruppo-ricevente]') as HTMLElement | null;
    const siglaSelect = this.$('[data-field="sigla"]') as HTMLSelectElement | null;
    const riceventeSelect = this.$('[data-field="siglaOrigine"]') as HTMLSelectElement | null;
    const modalitaSelect = this.$('[data-field="modalitaRicezione"]') as HTMLSelectElement | null;
    const modalitaOpzionale = this.$('[data-modalita-opzionale]') as HTMLElement | null;

    if (gruppoSigla) gruppoSigla.style.display = isGenerico ? 'none' : '';
    if (gruppoRicevente) gruppoRicevente.style.display = isGenerico ? '' : 'none';
    if (siglaSelect) {
      siglaSelect.required = !isGenerico;
      if (isGenerico) siglaSelect.value = '';
    }
    if (riceventeSelect) {
      riceventeSelect.required = isGenerico;
      if (!isGenerico) riceventeSelect.value = '';
    }
    if (modalitaSelect) modalitaSelect.required = isGenerico;
    if (modalitaOpzionale) modalitaOpzionale.style.display = isGenerico ? 'none' : '';
  }

  // --- Verifica e salvataggio ---

  private async verificaESalva(): Promise<void> {
    if (!this.lavoratoreSelezionato) return;

    const dati = this.raccogliDati();
    if (!dati) return;

    // GENERICO non ha bisogno di verifica
    if (dati.tipo === 'GENERICO') {
      await this.salva();
      return;
    }

    const submitBtn = this.$('[data-submit-btn]') as HTMLButtonElement | null;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Verifica...';
    }

    const result = await this.api.disdette.verifica({
      lavoratoreId: this.lavoratoreSelezionato.id,
      cassa: dati.cassa as 'CE' | 'EC',
      tipo: dati.tipo as 'INVIATA' | 'RICEVUTA' | 'GENERICO',
      sigla: dati.sigla,
    });

    if (!result.ok) {
      this.mostraErrore(result.error);
      this.resetSubmitBtn();
      return;
    }

    if (result.data.warnings.length > 0) {
      this.mostraWarnings(result.data.warnings);
      this.resetSubmitBtn();
      return;
    }

    await this.salva();
  }

  private async salva(): Promise<void> {
    if (!this.lavoratoreSelezionato) return;

    const dati = this.raccogliDati();
    if (!dati) return;

    const submitBtn = this.$('[data-submit-btn]') as HTMLButtonElement | null;
    const salvaComunqueBtn = this.$('[data-action="salva-comunque"]') as HTMLButtonElement | null;
    const btnAttivo = salvaComunqueBtn && this.warningsVisibili ? salvaComunqueBtn : submitBtn;

    if (btnAttivo) {
      btnAttivo.disabled = true;
      btnAttivo.innerHTML = '<span class="spinner-border spinner-border-sm me-1"></span>Salvataggio...';
    }

    const comuni = {
      cassa: dati.cassa as 'CE' | 'EC',
      tipo: dati.tipo as 'INVIATA' | 'RICEVUTA' | 'GENERICO',
      sigla: dati.sigla || undefined,
      siglaOrigine: dati.siglaOrigine || undefined,
      modalitaRicezione: (dati.modalitaRicezione || undefined) as ModalitaRicezione | undefined,
      dataDisdetta: dati.dataDisdetta,
    };
    const result = this.disdettaId
      ? await this.api.disdette.aggiorna(this.disdettaId, { ...comuni, note: dati.note || null })
      : await this.api.disdette.crea({
        ...comuni,
        lavoratoreId: this.lavoratoreSelezionato.id,
        note: dati.note || undefined,
      });

    if (!result.ok) {
      this.mostraErrore(result.error);
      if (btnAttivo) {
        btnAttivo.disabled = false;
        btnAttivo.innerHTML = '<i class="bi bi-check-circle me-1"></i>Salva comunque';
      }
      this.resetSubmitBtn();
      return;
    }

    Weext.toast(this.disdettaId ? 'Disdetta aggiornata' : 'Disdetta inserita con successo', { type: 'success' });

    const detail = { type: this.disdettaId ? 'disdetta-aggiornata' : 'disdetta-creata', id: result.data.id };
    setTimeout(() => this.notifyAndClose('disdetta-saved', detail), 800);
  }

  // --- Raccolta dati form ---

  private raccogliDati(): DatiForm | null {
    const val = (campo: string): string =>
      (this.$(`[data-field="${campo}"]`) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null)?.value || '';

    const dati: DatiForm = {
      cassa: val('cassa'),
      tipo: val('tipo'),
      sigla: val('sigla'),
      siglaOrigine: val('siglaOrigine'),
      modalitaRicezione: val('modalitaRicezione'),
      dataDisdetta: val('dataDisdetta'),
      note: val('note'),
    };

    if (!dati.cassa || !dati.tipo || !dati.dataDisdetta) return null;
    if (dati.tipo === 'GENERICO') {
      if (!dati.siglaOrigine || !dati.modalitaRicezione) return null;
      dati.sigla = '';
    } else {
      if (!dati.sigla) return null;
      dati.siglaOrigine = '';
    }
    return dati;
  }

  // --- Warning ---

  private mostraWarnings(warnings: string[]): void {
    this.warningsVisibili = true;
    const container = this.$('[data-warnings]');
    if (!container) return;

    container.style.display = '';
    container.innerHTML =
      '<div class="alert alert-warning py-2">' +
      '<strong><i class="bi bi-exclamation-triangle me-1"></i>Attenzione</strong>' +
      '<ul class="mb-2 mt-1">' +
      warnings.map(w => `<li>${w}</li>`).join('') +
      '</ul>' +
      '<div class="d-flex gap-2">' +
      '<button class="btn btn-warning btn-sm" data-action="salva-comunque">' +
      '<i class="bi bi-check-circle me-1"></i>Salva comunque</button>' +
      '<button class="btn btn-outline-secondary btn-sm" data-action="modifica">' +
      '<i class="bi bi-pencil me-1"></i>Modifica</button>' +
      '</div>' +
      '</div>';
  }

  private resetWarnings(): void {
    this.warningsVisibili = false;
    const container = this.$('[data-warnings]');
    if (container) {
      container.style.display = 'none';
      container.innerHTML = '';
    }
  }

  // --- Errore ---

  private mostraErrore(messaggio: string): void {
    const container = this.$('[data-errore]');
    if (container) {
      container.innerHTML = `<div class="alert alert-danger alert-sm py-1 px-2">${messaggio}</div>`;
    }
  }

  // --- Reset bottone submit ---

  private resetSubmitBtn(): void {
    const btn = this.$('[data-submit-btn]') as HTMLButtonElement | null;
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = '<i class="bi bi-check-circle me-1"></i>Verifica e salva';
    }
  }
}
